import { List } from "@chakra-ui/react"
import React from "react"
import { DocumentAction, DocumentStatus, DocumentType } from "types/DocumentStatus"
import { RoutingInfo } from "types/RoutingInfo"
import { getOptionTitle } from "utils/AnswerUtils"
import { DoAnyApplyOptions } from "utils/DoAnyApplyOptions"
import {
  MemberIndicatedFollowingContainer,
  MemberIndicatedHeader,
  MemberIndicatedListItem
} from "utils/RoutingInfoStyles"
import { isCheckInIntake } from "../carrotplans/utils/isCheckInIntake"
import { EmployeeConditionAttributes } from "types/Employee"
import { SprintsCandidacyResponse } from "./hooks/useGetSprintsCandidacy"
import { NcqaEnrollmentResponse } from "./hooks/useGetNcqaEnrollment"

type MemberIndicatedFollowingPropTypes = {
  currentRoutingInfo: RoutingInfo
  allMedicalQuestionsSkipped: boolean
  employeeConditionAttributes?: EmployeeConditionAttributes
  documentStatuses: Array<DocumentStatus>
  sprintsCandidacyStatus: SprintsCandidacyResponse
  ncqaEnrollmentStatus?: NcqaEnrollmentResponse
}

function hasSkippedMedicalRecordConsent(documentStatuses: DocumentStatus[], documentType: DocumentType): boolean {
  return Boolean(
    documentStatuses?.find(
      (docStatus) =>
        docStatus.documentType === documentType &&
        (docStatus.documentAction === DocumentAction.SKIPPED || docStatus.documentAction === DocumentAction.PRESENTED)
    )
  )
}

export function MemberIndicatedFollowing({
  currentRoutingInfo,
  allMedicalQuestionsSkipped,
  employeeConditionAttributes,
  documentStatuses,
  sprintsCandidacyStatus,
  ncqaEnrollmentStatus
}: MemberIndicatedFollowingPropTypes): JSX.Element {
  const journeyChanged = currentRoutingInfo?.journeyChanged
  const isMemberCheckIn = isCheckInIntake(currentRoutingInfo?.requestSource)
  const isSensitiveScenario = currentRoutingInfo?.isStillInPrevJourneyStage === "SENSITIVE_SCENARIO"
  const isNoCarrotPlanNeeded = currentRoutingInfo?.sensitiveScenarioSupport === "NO_CARROT_PLAN_NEEDED"
  const doAnyApply = currentRoutingInfo?.doAnyApply
  const showIvfFlag =
    currentRoutingInfo?.journey === "ASSISTED_REPRODUCTION" && employeeConditionAttributes?.hasIvfRequirements
  const isRequestNewerThanActiveCarrotPlan = currentRoutingInfo?.isRequestNewerThanActiveCarrotPlan

  // Skipped medical record consents
  const hasSkippedArtConsent = hasSkippedMedicalRecordConsent(documentStatuses, DocumentType.ART)
  const hasSkippedPregnancyConsent = hasSkippedMedicalRecordConsent(documentStatuses, DocumentType.PREGNANCY)
  const hasSkippedFertilityPreservationConsent = hasSkippedMedicalRecordConsent(
    documentStatuses,
    DocumentType.PRESERVATION
  )
  const hasSkippedAnyMedicalRecordConsent =
    hasSkippedArtConsent || hasSkippedPregnancyConsent || hasSkippedFertilityPreservationConsent

  // Do any apply
  const answeredDoAnyApply = doAnyApply && doAnyApply.length > 0
  const isMemberCandidateForSprints = sprintsCandidacyStatus?.memberIsSprintsCandidate
  const isPartnerCandidateForSprints = sprintsCandidacyStatus?.partnerIsSprintsCandidate
  const isEnrolledInNcqa = ncqaEnrollmentStatus?.isEnrolled

  const shouldShowMemberIndicated =
    answeredDoAnyApply ||
    showIvfFlag ||
    allMedicalQuestionsSkipped ||
    journeyChanged ||
    isMemberCandidateForSprints ||
    isPartnerCandidateForSprints ||
    hasSkippedAnyMedicalRecordConsent ||
    isMemberCheckIn ||
    isSensitiveScenario ||
    isRequestNewerThanActiveCarrotPlan ||
    isNoCarrotPlanNeeded ||
    isEnrolledInNcqa

  return shouldShowMemberIndicated ? (
    <MemberIndicatedFollowingContainer>
      <MemberIndicatedHeader>Please note:</MemberIndicatedHeader>
      <List>
        {isMemberCheckIn && <MemberIndicatedListItem>Member checked in</MemberIndicatedListItem>}
        {isSensitiveScenario && <MemberIndicatedListItem>Member indicated sensitive scenario</MemberIndicatedListItem>}
        {isNoCarrotPlanNeeded && <MemberIndicatedListItem>Member DOES NOT want a Carrot Plan</MemberIndicatedListItem>}
        {isMemberCandidateForSprints && <MemberIndicatedListItem>Member qualified for Sprints</MemberIndicatedListItem>}
        {isPartnerCandidateForSprints && (
          <MemberIndicatedListItem>Partner qualified for Sprints</MemberIndicatedListItem>
        )}
        {isEnrolledInNcqa && <MemberIndicatedListItem>Enrolled in NCQA program</MemberIndicatedListItem>}
        {doAnyApply && (
          <>
            {doAnyApply
              .filter((item) => item !== DoAnyApplyOptions.find((option) => option.id === "NONE")?.id)
              .map((item) => (
                <MemberIndicatedListItem key={item}>{getOptionTitle(DoAnyApplyOptions, item)}</MemberIndicatedListItem>
              ))}
          </>
        )}
        {showIvfFlag && (
          <MemberIndicatedListItem>
            Interested in ART journey: Might be a candidate for IVF requirements
          </MemberIndicatedListItem>
        )}
        {journeyChanged && <MemberIndicatedListItem>Journey changed</MemberIndicatedListItem>}
        {allMedicalQuestionsSkipped && <MemberIndicatedListItem>Skipped medical questions</MemberIndicatedListItem>}
        {hasSkippedArtConsent && (
          <MemberIndicatedListItem>Skipped signing ART medical records consent</MemberIndicatedListItem>
        )}
        {hasSkippedPregnancyConsent && (
          <MemberIndicatedListItem>Skipped signing Pregnancy medical records consent</MemberIndicatedListItem>
        )}
        {hasSkippedFertilityPreservationConsent && (
          <MemberIndicatedListItem>
            Skipped signing Fertility Preservation medical records consent
          </MemberIndicatedListItem>
        )}
        {isRequestNewerThanActiveCarrotPlan && (
          <MemberIndicatedListItem>
            No Carrot Plan has been published since these routing answers
          </MemberIndicatedListItem>
        )}
      </List>
    </MemberIndicatedFollowingContainer>
  ) : null
}
